import { styled } from '@mui/material/styles';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableCell,
  TableContainer,
  TableHead,
  TableRow as MuiTableRow,
  Chip,
  Divider,
} from '@mui/material';

export const ConfirmationContainer = styled(Box)(({ theme }) => ({
  // Contenedor principal
}));

export const ConfirmationHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(3),
}));

export const ConfirmationTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.125rem', // text-lg
  fontWeight: 600,
  color: theme.palette.text.primary,
  margin: 0,
}));

export const ConfirmationDate = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem', // text-sm
  color: theme.palette.text.secondary,
  backgroundColor: theme.palette.background.paper,
  padding: `${theme.spacing(0.5)} ${theme.spacing(1.5)}`,
  borderRadius: theme.shape.borderRadius * 2, // rounded-full
  border: `1px solid ${theme.palette.divider}`,
}));

export const SummaryCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  boxShadow: theme.shadows[1],
  border: `1px solid ${theme.palette.divider}`,
}));

export const SummaryCardContent = styled(CardContent)(({ theme }) => ({
  padding: theme.spacing(3),
}));

export const SummaryGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
  gap: theme.spacing(2),
  marginBottom: theme.spacing(3),
}));

export const SummaryItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  padding: theme.spacing(2.5),
  backgroundColor: theme.palette.background.paper,
  borderRadius: '12px',
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: theme.palette.mode === 'dark' ? 'none' : '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
  transition: 'all 0.2s ease',
  '&:hover': {
    borderColor: theme.palette.text.secondary,
    boxShadow: theme.palette.mode === 'dark' ? 'none' : '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
  },
}));

export const SummaryLabel = styled(Typography)(({ theme }) => ({
  fontSize: '0.75rem',
  color: theme.palette.text.secondary,
  textTransform: 'uppercase',
  fontWeight: 600,
  letterSpacing: '0.05em',
  marginBottom: theme.spacing(1),
}));

export const SummaryValue = styled(Typography)(({ theme }) => ({
  fontSize: '1.5rem',
  fontWeight: 700,
  color: theme.palette.text.primary,
}));

export const LocationInfo = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginTop: theme.spacing(2),
}));

export const LocationIcon = styled('svg')(({ theme }) => ({
  width: '1.125rem',
  height: '1.125rem',
  color: theme.palette.text.secondary,
  marginRight: theme.spacing(1),
  flexShrink: 0,
}));

export const LocationText = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  fontWeight: 400,
  color: theme.palette.text.secondary,
}));

export const ProductsTableContainer = styled(TableContainer)(({ theme }) => ({
  marginTop: theme.spacing(2),
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
}));

export const ProductsTable = styled(Table)(({ theme }) => ({
  '& .MuiTableCell-root': {
    borderBottom: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(1.5),
  },
}));

export const TableHeader = styled(TableHead)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  '& .MuiTableCell-root': {
    color: theme.palette.text.secondary,
    fontWeight: 600,
    fontSize: '0.95rem',
    letterSpacing: 0.8,
    textTransform: 'capitalize',
    borderBottom: `1px solid ${theme.palette.divider}`,
    background: 'transparent',
    paddingTop: theme.spacing(0.8),
    paddingBottom: theme.spacing(0.8),
  },
}));

export const TableHeaderCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 600,
  fontSize: '0.95rem',
  color: theme.palette.text.secondary,
  letterSpacing: 0.8,
  textTransform: 'capitalize',
}));

export const TableBodyCell = styled(TableCell)(({ theme }) => ({
  fontSize: '0.875rem', // text-sm
  color: theme.palette.text.primary,
}));

export const ProductNameCell = styled(TableBodyCell)(({ theme }) => ({
  fontWeight: 500,
  color: theme.palette.text.primary,
}));

export const ProductCodeCell = styled(TableBodyCell)(({ theme }) => ({
  fontFamily: 'monospace',
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.secondary,
}));

export const QuantityCell = styled(TableBodyCell)(({ theme }) => ({
  textAlign: 'center',
  fontWeight: 500,
  color: theme.palette.primary.main,
}));

export const StatusChip = styled(Chip)(({ theme }) => ({
  fontSize: '0.75rem', // text-xs
  height: '1.5rem', // h-6
  fontWeight: 500,
}));

export const ValidationError = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  backgroundColor: theme.palette.error.light,
  color: theme.palette.error.contrastText,
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.error.main}`,
  marginTop: theme.spacing(2),
}));

export const ValidationErrorTitle = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem', // text-sm
  fontWeight: 600,
  marginBottom: theme.spacing(1),
}));

export const ValidationErrorList = styled('ul')(({ theme }) => ({
  margin: 0,
  paddingLeft: theme.spacing(2),
  fontSize: '0.875rem', // text-sm
}));

export const ValidationErrorItem = styled('li')(({ theme }) => ({
  marginBottom: theme.spacing(0.5),
}));

export const EmptyState = styled(Box)(({ theme }) => ({
  padding: theme.spacing(4),
  textAlign: 'center',
  color: theme.palette.text.secondary,
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius,
  border: `1px dashed ${theme.palette.divider}`,
}));

export const EmptyStateIcon = styled('svg')(({ theme }) => ({
  width: '3rem', // w-12
  height: '3rem', // h-12
  color: theme.palette.grey[400],
  marginBottom: theme.spacing(2),
}));

export const EmptyStateTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1rem', // text-base
  fontWeight: 500,
  color: theme.palette.text.primary,
  marginBottom: theme.spacing(1),
}));

export const EmptyStateDescription = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem', // text-sm
  color: theme.palette.text.secondary,
}));

export const SectionDivider = styled(Divider)(({ theme }) => ({
  margin: `${theme.spacing(2)} 0`,
}));

// Re-exportar TableBody de Material-UI
export { TableBody } from '@mui/material';

// Styled TableRow (hover global vía MuiTableRow en theme)
export const TableRow = styled(MuiTableRow)(({ theme }) => ({
  transition: 'background 0.2s',
}));
